// web/src/features/caf/CafNew.jsx
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'

import { getNextCafNumberFS, createCaf } from '@/services/firestore/cafService'
import { listCommunitiesFS } from '@/services/firestore/communityService'
import { refreshFacilityTypes } from '@/services/firestore/facilityTypeService'
import { createTaskForCafDraftFS } from '@/services/firestore/taskService'
import { getMyProfileFS } from '@/services/firestore/usersService'
import useSession from '@/hooks/useSession'

const db = (() => {
  try { return getFirestore(getApp()) } catch { return getFirestore() }
})()

/* ---------------- helpers ---------------- */
function lc(s){ return (s || '').toString().trim().toLowerCase() }

/* ---------------- component ---------------- */
export default function CafNew(){
  const nav = useNavigate()

  const [communities, setCommunities] = React.useState([])
  const [facilityTypes, setFacilityTypes] = React.useState([])
  const [communityCode, setCommunityCode] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [facilityType, setFacilityType] = React.useState('')
  const [contract, setContract] = React.useState('')
  const [notBudgeted, setNotBudgeted] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [err, setErr] = React.useState('')
  const [liveCommunities, setLiveCommunities] = React.useState([])

  const sess = useSession()
  const myCommunities = React.useMemo(() => {
    if (sess?.allCommunities === true) return new Set()
    const source = liveCommunities.length
      ? liveCommunities
      : (Array.isArray(sess?.communities) ? sess.communities : [])
    return new Set(source.map(lc))
  }, [liveCommunities, sess])

  React.useEffect(() => {
    let unsubscribe = null
    let alive = true
    ;(async () => {
      try {
        const profile = await getMyProfileFS()
        if (!alive || !profile?.email) return
        setLiveCommunities(
          Array.isArray(profile.communities) ? profile.communities : [],
        )
        unsubscribe = onSnapshot(
          doc(db, 'users', lc(profile.email)),
          (snap) => {
            if (!alive || !snap.exists()) return
            const data = snap.data() || {}
            setLiveCommunities(
              Array.isArray(data.communities) ? data.communities : [],
            )
          },
        )
      } catch (e) {
        console.warn('[CafNew] live profile subscribe failed:', e)
      }
    })()
    return () => {
      alive = false
      if (unsubscribe) unsubscribe()
    }
  }, [])

  // load communities + facility types
  React.useEffect(() => {
    ;(async () => {
      try {
        const [comms, types] = await Promise.all([
          listCommunitiesFS(),
          refreshFacilityTypes(),
        ])

        // scope communities by session
        const scoped = (Array.isArray(comms) ? comms : []).filter(c => {
          if (!myCommunities.size) return true
          const code = lc(c.code || c.communityCode)
          const name = lc(c.communityName || c.name || c.code)
          return myCommunities.has(code) || myCommunities.has(name)
        })

        setCommunities(scoped)

        const activeTypeNames = (types || [])
          .filter(t => t.isActive !== false)
          .map(t => t.name)
          .filter(Boolean)
          .sort((a,b) => a.localeCompare(b))
        setFacilityTypes(activeTypeNames)

        // if exactly one community, preselect
        if (scoped.length === 1) {
          const only = scoped[0]
          setCommunityCode(only.code || only.communityCode)
        }
      } catch (e) {
        console.error('[CafNew] init failed:', e)
        setErr('Failed to load reference data.')
      }
    })()
  }, [myCommunities])

  const communityOptions = React.useMemo(() => {
  const base = (communities || []).map(c => ({
    code: c.code || c.communityCode,
    name: c.communityName || c.name || c.code,
  }))

  // Sort alphabetically by the display name
  return base.sort((a, b) => a.name.localeCompare(b.name))
}, [communities])

  function currentCommunity(){
    return (communities || []).find(
      c => (c.code || c.communityCode) === communityCode
    )
  }

  async function onCreate(){
    setErr('')
    const comm = currentCommunity()

    if (!comm || !communityCode){
      setErr('Please select a community.')
      return
    }
    if (!title.trim()){
      setErr('Please enter a title.')
      return
    }
    if (!facilityType){
      setErr('Please select a facility type.')
      return
    }

    let contractPrice = null
    if (notBudgeted) {
      contractPrice = 0
    } else if (contract !== ''){
      const n = Number(contract)
      if (!Number.isFinite(n) || n <= 0){
        setErr('Budget amount must be greater than $0 unless the project is marked Not Budgeted.')
        return
      }
      contractPrice = n
    }

    try {
      setCreating(true)

      // 1) allocate CAF number from server-side helper
      const cafNum = await getNextCafNumberFS(comm.code || comm.communityCode)

      // 2) build new CAF payload (draft)
      const nowIso = new Date().toISOString()
      const sessEmail = sess?.email || ''
      const sessName  = sess?.displayName || ''

      const newCaf = {
        number: cafNum,                        // human-readable CAF number
        communityCode: comm.code || comm.communityCode,
        communityName: comm.communityName || comm.name || comm.code,
        title: title.trim(),
        facilityType,
        contractPrice,
        notBudgeted,
        projectBudget: contractPrice,
        projectCompletionDate: '',
        completionDateReminder: {
          sentForDate: '',
          sentAt: null,
          sentTo: [],
        },
        status: 'draft',
        stage: 'Manager',
        proposals: [],
        proposalsCount: 0,
        messages: [],
        files: [],
        docs: [],
        documents: [],
        attachments: [],
        timeline: [
          {
            ts: nowIso,
            event: `CAF created as draft — ${sessName || sessEmail || 'user'}`,
          },
        ],
        approvals: {
          sequence: ['Manager','Regional Manager'],
          history: [],
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByEmail: sessEmail,
        createdByName: sessName,
      }

      // 3) create in Firestore (service handles details)
      const cafId = await createCaf(newCaf)

      // 4) optional: create initial "draft" task for creator
      try {
        await createTaskForCafDraftFS({
          cafId,
          cafNumber: cafNum,
          communityCode: newCaf.communityCode,
          communityName: newCaf.communityName,
          title: newCaf.title,
          assigneeEmail: sessEmail,
          assigneeName: sessName,
        })
      } catch (e) {
        console.warn('[CafNew] draft task create failed (non-fatal):', e)
      }

      // 5) go to the CAF detail page
      nav(`/dev/caf/${encodeURIComponent(cafId)}`)
    } catch (e) {
      console.error('[CafNew] CAF create failed:', e)
      setErr('Failed to create CAF: Missing or insufficient permissions.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div>
      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    marginBottom: 22,
  }}
>
  <span>New Capital Approval Form</span>
</h2>

      {err && (
        <div
          className="card"
          style={{borderColor:'#b94a48', background:'#fff7f7', color:'#b94a48'}}
        >
          {err}
        </div>
      )}

      <div className="mobile-stack-grid card" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
        <div>
          <label>Community (required)</label>
          <select
            value={communityCode}
            onChange={e => setCommunityCode(e.target.value)}
          >
            <option value="">— Select Community —</option>
            {communityOptions.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label>Title (required)</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Short description of the work"
          />
        </div>

        <div>
          <label>Facility Type (required)</label>
          <select
            value={facilityType}
            onChange={e => setFacilityType(e.target.value)}
          >
            <option value="">— Select Facility Category —</option>
            {facilityTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div>
          <label>Budget (USD)</label>
          <input
            type="number"
            inputMode="decimal"
            value={contract}
            onChange={e => setContract(e.target.value)}
            placeholder={notBudgeted ? 'Not budgeted' : 'e.g., 45000'}
            disabled={notBudgeted}
          />
          <div style={{ marginTop: 8 }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={notBudgeted}
                onChange={(e) => {
                  setNotBudgeted(e.target.checked)
                  if (e.target.checked) setContract('')
                }}
              />
              Not Budgeted
            </label>
          </div>
          <div className="muted" style={{marginTop:4}}>
            {notBudgeted
              ? 'This CAF will submit with a $0 budget so future spend is treated as overage.'
              : 'Enter a positive budget amount, or mark the project as Not Budgeted.'}
          </div>
        </div>
      </div>

      <div style={{marginTop:16, display:'flex', gap:8}}>
        <button
          className="primary"
          onClick={onCreate}
          disabled={creating}
        >
          {creating ? 'Creating…' : 'Create'}
        </button>
        <button
          className="secondary"
          onClick={() => nav('/dev/caf')}
          disabled={creating}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
